import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense } from "react";
import PATH from "../urls/path";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import { setAuthStatus, setUser } from "../redux/slices/authSlice";
import { setListConfig } from "../redux/slices/environmentSlice";
import { advancedResponseValidator, generateNewAccessToken, get, getAll, responseValidator } from "../scripts/api";
import API from "../urls/api";
import { getObject, getSharedObject, updateQuota } from "../utils/component/fileManagers/fileManager.function";
import { setCurrentResource } from "../redux/slices/pathSlice";
import { ConfirmPlan } from "../pages/plan/ConfirmPlan/ConfirmPlan.index";
import { authToken } from "../utils/storage/storage";
import { addZeroToStart, getUserProfile, getWebEnvConfig } from "../scripts/helpers";
import { ENVIRONMENT } from "../environment";
import Loading from "../utils/component/loading/loading.index";
import { setNewLoginStatus } from "../redux/slices/newLoginSlice";
import { shallowEqual, useDispatch } from "react-redux";
import Layout from "../pages/layout/layout/layout.index";
import Loader from "../utils/component/loader/loader.index";
import Upload from "../utils/component/upload/upload.index";
import SsoRedirect from "../pages/ssoRedirect/ssoRedirect.index";
import { setGiftPurchase } from "../redux/slices/fileManagerSlice";

const Collabora = lazy(() => import("../pages/Collabora"));
const Home = lazy(() => import("../pages/home/home.index"));
const Dashboard = lazy(() => import("../pages/dashboard/dashboard.index"));
const Report = lazy(() => import("../pages/report/report.index"));
const Shared = lazy(() => import("../pages/shared/shared.index"));
const Trash = lazy(() => import("../pages/trash/trash.index"));
const Favorite = lazy(() => import("../pages/favorite/favorite.index"));
const PlanList = lazy(() => import("../pages/plan/planList/planList.index"));
const Plan = lazy(() => import("../pages/plan/main/plan.index"));
const PlanHistory = lazy(() => import("../pages/plan/planHistory/main/planHistory.index"));
const ResultPay = lazy(() => import("../pages/plan/resultPay/resultPay.index"));
const Contact = lazy(() => import("../pages/contact/contact.index"));
const PublicFolder = lazy(() => import("../pages/publicFolder/publicFolder"));
const Search = lazy(() => import("../pages/search/search.index"));
const Settings = lazy(() => import("../pages/settings/settings.index"));
const Error404 = lazy(() => import("../pages/errors/error404.index"));
const EwanoConfirm = lazy(() => import("../pages/plan/ConfirmPlan/EwanoConfirm.index"));
const DuplicatedFiles = lazy(() =>
    import("../pages/layout/duplicatedFiles/duplicatedFiles.index").then((module) => ({ default: module.DuplicatedFiles }))
);
const FileLinks = lazy(() => import("../pages/fileLinks/fileLinks.index"));
const Feedback = lazy(() => import("../pages/feedback/feedback.index"));

export default function MyRoutes(): JSX.Element {
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { authStatus } = useAppSelector(({ auth: { authStatus } }) => ({ authStatus }), shallowEqual);

    useEffect(() => {
        if (getWebEnvConfig("maintenance") === "true") {
            window.location.href = ENVIRONMENT.landingBaseUrl;
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const isRegister = params.get("isRegister");
        if (isRegister === "true") dispatch(setNewLoginStatus(true));
        else dispatch(setNewLoginStatus(false));
    }, []);

    const refreshToken = (refresh?: string) => {
        generateNewAccessToken(refresh).then((token: any) => {
            fetch(API.auth.profile(), {
                method: "get",
                headers: {
                    Authorization: "Bearer " + token.access
                }
            })
                .then((response) => {
                    return response.json();
                })
                .then((data) => {
                    dispatch(setUser({ ...data, phone: addZeroToStart(data.phone) }));
                    dispatch(setAuthStatus("VALID"));
                });
        });
    };

    useEffect(() => {
        if (authToken.get()) {
            getUserProfile();
        } else {
            const params = new URLSearchParams(window.location.search);
            const refresh = params.get("refresh");
            if (refresh) {
                refreshToken(refresh);
            } else {
                if (location.pathname === PATH.publicFolder) {
                    dispatch(setAuthStatus("INVALID"));
                } else {
                    window.location.replace(`${ENVIRONMENT.landingBaseUrl}auth/login?redirect=${btoa(window.location.href)}`);
                }
            }
        }
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("path")) {
            if (location.pathname === PATH.shared) {
                getSharedObject(params.get("path")!).then((data) => {
                    dispatch(setCurrentResource(data));
                });
            } else {
                getObject(Number(params.get("path")!)).then((data) => {
                    dispatch(setCurrentResource(data));
                });
            }
        } else {
            dispatch(setCurrentResource(null));
        }
    }, [location.search, location.pathname]);

    useEffect(() => {
        if (authStatus === "VALID") {
            updateQuota();
            get(
                (data, status) => {
                    if (responseValidator(status)) {
                        dispatch(setListConfig(data.results));
                    }
                },
                API.list_config(),
                { limit: 1000 }
            );

            getAll((data, status) => {
                if (advancedResponseValidator(data, status)) dispatch(setGiftPurchase(data.result));
            }, API.plan.activeUserGifts());
        }
    }, [authStatus]);

    return (
        <>
            {authStatus === "PENDING" && <Loading />}
            {authStatus === "VALID" && <Upload />}

            <Routes>
                {authStatus === "INVALID" && (
                    <Route>
                        <Route element={<Layout general={false} />}>
                            <Route
                                path={PATH.publicFolder}
                                element={
                                    <Suspense fallback={<Loading />}>
                                        <Shared />
                                    </Suspense>
                                }
                            />
                        </Route>
                        <Route path={"/"} element={<Navigate to={PATH.auth.login} />} />
                        <Route
                            path={"*"}
                            element={
                                <Suspense fallback={<Loader />}>
                                    <Error404 />
                                </Suspense>
                            }
                        />
                    </Route>
                )}
                {authStatus === "VALID" && (
                    <Route>
                        <Route path={`/`} element={<Navigate to={PATH.dashboard} />} />
                    </Route>
                )}
                {authStatus === "VALID" && (
                    <>
                        <Route
                            path={"*"}
                            element={
                                <Suspense fallback={<Loader />}>
                                    <Error404 />
                                </Suspense>
                            }
                        />
                        {/* <Route
                            path={PATH.excelEditor()}
                            element={
                                <Suspense fallback={<Loading />}>
                                    <ExcelEditor />
                                </Suspense>
                            }
                        /> */}
                        <Route
                            element={
                                <Suspense>
                                    <Layout general={true} />
                                </Suspense>
                            }
                        >
                            <Route
                                path={PATH.dashboard}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <Dashboard />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.files}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <Home />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.report}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <Report />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.publicFolder}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <Shared />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.trash}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <Trash />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.shared}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <Shared />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.favorite}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <Favorite />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.planRecommendation}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <PlanList />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.plan}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <Plan />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.duplicatedFiles}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <DuplicatedFiles />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.planHistory}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <PlanHistory />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.contact}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <Contact />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.public}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <PublicFolder />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.search}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <Search />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.settings}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <Settings />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.links()}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <FileLinks />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.sso}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <SsoRedirect />
                                    </Suspense>
                                }
                            />
                            <Route
                                path={PATH.feedback}
                                element={
                                    <Suspense fallback={<Loader />}>
                                        <Feedback />
                                    </Suspense>
                                }
                            />
                        </Route>
                    </>
                )}
                {authStatus === "VALID" && (
                    <Route>
                        <Route
                            path={PATH.resultPay}
                            element={
                                <Suspense fallback={<Loading />}>
                                    <ResultPay />
                                </Suspense>
                            }
                        />
                        <Route
                            path={PATH.confirmPay()}
                            element={
                                <Suspense fallback={<Loading />}>
                                    <ConfirmPlan />
                                </Suspense>
                            }
                        />
                        <Route
                            path={PATH.ewanoConfirm()}
                            element={
                                <Suspense fallback={<Loading />}>
                                    <EwanoConfirm />
                                </Suspense>
                            }
                        />
                    </Route>
                )}
                {getWebEnvConfig("COLLABORA") === "true" && <Route path={PATH.collabora} element={<Collabora />} />}
            </Routes>
        </>
    );
}
